import React, { useState } from "react";
import {
    Table,
    Card,
    Placeholder,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useReportTemplates } from "@features/report/template/hooks/useReportTemplates";
import { SearchBar, FilterField } from "@core/components/SearchBar";
import {
    NAME_SORT_FILTER_FIELD,
    SORT_BY_REPORT_TEMPLATE_NAME,
    SORT_DIR_ASC,
    countContentFilters,
    filtersWithoutSort,
    normalizeSortDir,
    readSortDirFromSearchParams,
    withCatalogSort,
} from "@core/constants/listSort";
import { useDebounce } from "@core/hooks/useDebounce";
import { useSearchUrlSync } from "@core/hooks/useSearchUrlSync";
import NoResult from "@core/components/NoResult";
import TableActionButton from "@core/components/TableActionButton";
import TableActions from "@core/components/TableActions";
import StatusPill from "@core/components/StatusPill";
import EntityCard from "@core/components/EntityCard";
import { FiEdit2, FiSlash, FiCheckCircle } from "react-icons/fi";
import { ReportTemplateTableProps } from "@features/report/template/types/reportTemplateTableProps";
import { ReportTemplate } from "@features/report/template/types/reportTemplate";
import PaginationBar from "@core/components/PaginationBar";
import TablePaginationFooter, { getPaginationRange } from "@core/components/TablePaginationFooter";
import UpdateTemplateModal from "./UpdateTemplateModal";
import ChangeOriginScopeModal from "./ChangeOriginScopeModal";
import AssignTemplateActivityActivityModal from "./assignTemplateActivityActivityModal";
import AssignTemplateSubGroupModal from "./AssignTemplateSubGroupModal";
import PreviewReportTemplateModal from "./previewReportTemplateModal";
import StatusChangeConfirmModal, { StatusChangeKind } from "@features/admin/finance/components/StatusChangeConfirmModal";
import { useReportTemplateActions } from "@features/report/template/hooks/useReportTemplateActions";
import { usePermissions } from "@features/entity/hooks/usePermissions";
import { TEMPLATE_CATALOG_MANAGE_CODES, getTemplateCatalogCodesForOrigin, getTemplateOriginUpdateCode } from "@features/entity/types/actionCodes";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { ErrorResponse } from "@core/types/errorResponse";
import { OriginScope } from "../types/reportTemplate";

const TEMPLATE_STATUS_ACTION_CODES = [
    ...TEMPLATE_CATALOG_MANAGE_CODES,
];

const ORIGIN_SCOPE_LABELS: Record<string, string> = {
    CHURCH: 'Church',
    DISTRICT: 'District',
    NATION: 'Nation',
};

const REPORT_TEMPLATE_FILTER_FIELDS: FilterField[] = [
    NAME_SORT_FILTER_FIELD,
    { name: 'reportTemplateName', label: 'Template Name', type: 'text', placeholder: 'Filter by template name', colWidth: 6 },
    { name: 'description',        label: 'Description',   type: 'text', placeholder: 'Filter by description',   colWidth: 6 },
    { name: 'activityName',       label: 'Activity Name', type: 'text', placeholder: 'Filter by activity name', colWidth: 6 },
    { name: 'kpiName',            label: 'KPI Name',      type: 'text', placeholder: 'Filter by KPI name',      colWidth: 6 },
    { name: 'originScope',        label: 'To be completed by', type: 'select', options: [
        { value: 'CHURCH', label: 'Church' },
        { value: 'DISTRICT', label: 'District' },
        { value: 'NATION', label: 'Nation' },
    ], colWidth: 6 },
    { name: 'isActive',          label: 'Status',        type: 'select', options: [{ value: '1', label: 'Active' }, { value: '0', label: 'Inactive' }], colWidth: 6 },
];

const ReportTemplateTable: React.FC<ReportTemplateTableProps> = ({
    departmentId,
    showPagination = false,
    nationId,
    refreshSignal,
}) => {

    const [searchParams] = useSearchParams();
    const resolvedDepartmentId = departmentId ?? Number(searchParams.get("did"));

    const [page, setPage] = useState(() => Number(searchParams.get('page')) || 1);
    const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') ?? '');
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() => {
        const f: Record<string, any> = {};
        REPORT_TEMPLATE_FILTER_FIELDS.forEach(({ name }) => {
            const v = searchParams.get(name);
            if (v) f[name] = v;
        });
        f.sortDir = readSortDirFromSearchParams(searchParams);
        return f;
    });
    const debouncedQuery = useDebounce(searchQuery, 300);

    useSearchUrlSync({ query: debouncedQuery, filters: activeFilters, page });

    const [selectedTemplate, setSelectedTemplate] = useState<any | null>(null);
    const [show, setShowUpdateModal] = useState(false);
    const [showOriginScopeModal, setShowOriginScopeModal] = useState(false);
    const [showAssignTemplateActivityModal, setShowAssignTemplateActivityModal] = useState(false);
    const [showAssignTemplateSubGroupModal, setShowAssignTemplateSubGroupModal] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [statusConfirm, setStatusConfirm] = useState<{ template: ReportTemplate; kind: StatusChangeKind } | null>(null);

    const showError = useApiError();
    const { busy: statusBusy, doTemplateDisable, doTemplateEnable } = useReportTemplateActions();

    // ADMIN-only enable/disable gated on NATION-scoped action codes.
    const { hasPermission } = usePermissions({
        scopeType: "NATION",
        scopeId: nationId || undefined,
        actionCodes: TEMPLATE_STATUS_ACTION_CODES,
    });
    const canDisable = (originScope?: OriginScope) => {
        const codes = originScope ? getTemplateCatalogCodesForOrigin(originScope) : undefined;
        return codes ? hasPermission(codes.disable) : false;
    };
    const canEnable = (originScope?: OriginScope) => {
        const codes = originScope ? getTemplateCatalogCodesForOrigin(originScope) : undefined;
        return codes ? hasPermission(codes.enable) : false;
    };
    const canChangeOriginScope = (originScope?: OriginScope) => {
        if (!originScope) return false;
        return hasPermission(getTemplateOriginUpdateCode(originScope));
    };

    const canEditTemplateByOrigin = (originScope?: OriginScope): boolean => {
        if (!originScope) return false;
        const codes = getTemplateCatalogCodesForOrigin(originScope);
        return codes ? hasPermission(codes.update) : false;
    };

    const appliedFilters = Object.fromEntries(
        Object.entries(filtersWithoutSort(activeFilters)).filter(([, v]) => v !== '' && v != null)
    );
    const activeFilterCount = countContentFilters(activeFilters);
    const hasSearch = !!(debouncedQuery || activeFilterCount);

    // Fetch report templates using custom hook
    const { current_page, data, from, last_page, per_page, total, loading, reload } = useReportTemplates(
        withCatalogSort(
            {
                departmentId: resolvedDepartmentId,
                page,
                ...(!hasSearch ? { all: true } : {}),
                freetext: debouncedQuery || undefined,
                ...appliedFilters,
                sortDir: normalizeSortDir(activeFilters.sortDir),
            },
            SORT_BY_REPORT_TEMPLATE_NAME
        )
    );

    // Reload when a sibling create succeeds (skip the initial mount).
    const didMountRef = React.useRef(false);
    React.useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        void reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);

    // Calculate total pages
    const totalPages = Math.max(1, Math.ceil(total / (per_page ?? 20)));
    const pageNumber = current_page ?? page;
    const { start, end } = getPaginationRange({
        from,
        page: pageNumber,
        perPage: per_page,
        total,
        currentCount: data.length,
    });

    const handleStatusConfirm = async () => {
        if (!statusConfirm || !nationId) return;
        const { template, kind } = statusConfirm;
        const response = kind === "disable"
            ? await doTemplateDisable(nationId, template.department_id, template.report_template_id)
            : await doTemplateEnable(nationId, template.department_id, template.report_template_id);

        if (response.isSuccess()) {
            showSuccess(response.getData()?.message ?? `Report template ${kind}d successfully.`);
            setStatusConfirm(null);
            void reload();
        } else {
            showError(response.getError() as ErrorResponse);
        }
    };

    // Shared by the desktop table row and the mobile card.
    const renderTemplateActions = (t: (typeof data)[number]) => {
        const canEditThisTemplate = canEditTemplateByOrigin(t.origin_scope);

        return (
            <TableActions aria-label={`Actions for template ${t.report_template_name}`}>
                {canEditThisTemplate && (
                    <TableActionButton
                        icon={<FiEdit2 aria-hidden="true" />}
                        label="Edit"
                        primary
                        onClick={() => { setSelectedTemplate(t); setShowUpdateModal(true); }}
                        data-testid={`edit-template-button-${t.report_template_id}`}
                    />
                )}
                {canEditThisTemplate && (
                    <TableActionButton
                        icon="journal-plus"
                        label="Assign Activity"
                        onClick={() => { setSelectedTemplate(t); setShowAssignTemplateActivityModal(true); }}
                    />
                )}
                {canEditThisTemplate && (
                    <TableActionButton
                        icon="collection"
                        label="Manage Sub Groups"
                        onClick={() => { setSelectedTemplate(t); setShowAssignTemplateSubGroupModal(true); }}
                    />
                )}
                <TableActionButton
                    icon="eye"
                    label="Preview"
                    primary
                    variant="outline-secondary"
                    onClick={() => { setSelectedTemplate(t); setShowPreviewModal(true); }}
                />
                {canChangeOriginScope(t.origin_scope) && t.is_active && (
                    <TableActionButton
                        icon="arrow-left-right"
                        label="Change who completes this"
                        variant="outline-secondary"
                        onClick={() => { setSelectedTemplate(t); setShowOriginScopeModal(true); }}
                        data-testid={`change-origin-scope-button-${t.report_template_id}`}
                    />
                )}
                {t.is_active && canDisable(t.origin_scope) && canEditThisTemplate && (
                    <TableActionButton
                        icon={<FiSlash />}
                        label="Disable"
                        variant="outline-secondary"
                        className="ntcog-table-action-btn--danger"
                        onClick={() => setStatusConfirm({ template: t, kind: "disable" })}
                        data-testid={`disable-template-button-${t.report_template_id}`}
                    />
                )}
                {!t.is_active && canEnable(t.origin_scope) && canEditThisTemplate && (
                    <TableActionButton
                        icon={<FiCheckCircle />}
                        label="Enable"
                        variant="outline-secondary"
                        className="ntcog-table-action-btn--success"
                        onClick={() => setStatusConfirm({ template: t, kind: "enable" })}
                        data-testid={`enable-template-button-${t.report_template_id}`}
                />
                )}
            </TableActions>
        );
    };

    return (
        <div>
            <div className="ntcog-list-search-section">
                <SearchBar
                    placeholder="Search report templates…"
                    filterFields={REPORT_TEMPLATE_FILTER_FIELDS}
                    initialQuery={searchQuery}
                    initialFilters={activeFilters}
                    activeFilterCount={activeFilterCount}
                    loading={loading}
                    onQueryChange={(q) => { setSearchQuery(q); setPage(1); }}
                    onFiltersApply={(f) => {
                        setActiveFilters({ ...f, sortDir: normalizeSortDir(f.sortDir) });
                        setPage(1);
                    }}
                    onClear={() => {
                        setSearchQuery('');
                        setActiveFilters({ sortDir: SORT_DIR_ASC });
                        setPage(1);
                    }}
                    data-testid="report-template-search-bar"
                />
            </div>
            <Card className="shadow-sm ntcog-card ntcog-list-table-card" data-testid="report-template-table-card">
                <Card.Body className="p-0">
                    <div className="px-3 pb-3 d-none d-md-block">
                        <Table responsive
                            hover
                            size="sm"
                            className="mb-0 align-middle table-striped ntcog-table" data-testid="report-template-table"
                        >
                            <thead className="table-light">
                                <tr className="small text-muted">
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Description</th>
                                    <th>To be completed by</th>
                                    <th>Status</th>
                                    <th className="text-end" style={{ width: 280 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className="table-group-divider">
                                {loading && Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={`ph-${i}`} data-testid={`skeleton-row-${i}`}>
                                        <td className="text-muted"><Placeholder xs={6} /></td>
                                        <td className="text-muted"><Placeholder xs={6} /></td>
                                        <td className="text-muted"><Placeholder xs={8} /></td>
                                        <td className="text-muted"><Placeholder xs={4} /></td>
                                        <td className="text-muted"><Placeholder xs={4} /></td>
                                        <td className="text-end"><Placeholder xs={3} /></td>
                                    </tr>
                                ))}
                                {!loading && data.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="py-5">
                                            <NoResult line_1="No Templates found." line_2="Try adjusting filters or create a new Report Template." />
                                        </td>
                                    </tr>
                                )}
                                {!loading && data.map((t: typeof data[number], i: number) => {
                                    const _id = Number(t.report_template_id);
                                    const rowNumber = (from != null) ? from + i : i + 1;
                                    return (
                                        <tr key={t.report_template_id}>
                                            <td>
                                                {rowNumber}
                                            </td>
                                            <td>{t.report_template_name}</td>
                                            <td>{t.description}</td>
                                            <td>{t.origin_scope ? ORIGIN_SCOPE_LABELS[t.origin_scope] || t.origin_scope : '—'}</td>
                                            <td><StatusPill active={!!t.is_active} /></td>
                                            <td className="text-end">
                                                {renderTemplateActions(t)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    {/* Mobile (< md): stacked cards */}
                    <div className="ntcog-card-list d-md-none" data-testid="report-template-cards">
                        {loading && Array.from({ length: 6 }).map((_, i) => (
                            <div key={`ph-card-${i}`} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`template-card-skeleton-${i}`}
                                    title={<Placeholder as="span" animation="glow"><Placeholder xs={6} /></Placeholder>}
                                    description={<Placeholder as="span" animation="glow"><Placeholder xs={8} /></Placeholder>}
                                />
                            </div>
                        ))}
                        {!loading && data.length === 0 && (
                            <NoResult line_1="No Templates found." line_2="Try adjusting filters or create a new Report Template." />
                        )}
                        {!loading && data.map((t: typeof data[number]) => (
                            <div key={t.report_template_id} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`template-card-${t.report_template_id}`}
                                    ariaLabel={`Report template ${t.report_template_name}`}
                                    title={<span className="fw-semibold">{t.report_template_name}</span>}
                                    status={<StatusPill active={!!t.is_active} />}
                                    actions={renderTemplateActions(t)}
                                    description={t.description || undefined}
                                />
                            </div>
                        ))}
                    </div>
                </Card.Body>
            </Card>



            {showPagination && (
                <TablePaginationFooter
                    className="mt-3"
                    testId="pagination-bar"
                    info={`Showing ${start} to ${end} of ${total} templates`}
                >
                    <PaginationBar
                        page={pageNumber}
                        totalPages={last_page ?? totalPages}
                        onPage={(p) => setPage(p)}
                    />
                </TablePaginationFooter>
            )}


            <UpdateTemplateModal
                show={show}
                onHide={() => setShowUpdateModal(false)}
                template={selectedTemplate}
                onUpdated={() => reload()}
            />

            <ChangeOriginScopeModal
                show={showOriginScopeModal}
                onHide={() => setShowOriginScopeModal(false)}
                template={selectedTemplate}
                onChanged={() => reload()}
            />

            <AssignTemplateActivityActivityModal
                show={showAssignTemplateActivityModal}
                onHide={() => setShowAssignTemplateActivityModal(false)}
                reportTemplate={selectedTemplate}
            />

            <AssignTemplateSubGroupModal
                show={showAssignTemplateSubGroupModal}
                onHide={() => setShowAssignTemplateSubGroupModal(false)}
                reportTemplate={selectedTemplate}
            />

            <PreviewReportTemplateModal
                show={showPreviewModal}
                onHide={() => setShowPreviewModal(false)}
                departmentId={resolvedDepartmentId}
                reportTemplateId={selectedTemplate?.report_template_id}
                nationId={nationId}
            />

            <StatusChangeConfirmModal
                show={!!statusConfirm}
                kind={statusConfirm?.kind ?? "disable"}
                entityLabel="report template"
                entityName={statusConfirm?.template.report_template_name ?? ""}
                busy={statusBusy}
                onConfirm={handleStatusConfirm}
                onCancel={() => setStatusConfirm(null)}
                bodyText={statusConfirm?.kind === "disable" ? (
                    <>
                        Are you sure you want to disable <strong>{statusConfirm?.template.report_template_name}</strong>? It can no longer be assigned to churches and churches can&apos;t submit reports against it. Historical reports are unaffected.
                    </>
                ) : undefined}
            />

        </div>
    );
};

export default ReportTemplateTable;


